// Shared seed-by-grass mapping: each supported species -> its enthusiast-grade
// catalog seed (data/guide-product-recommendations.ts) plus the "why it fits"
// reason, and the planting-time STARTER companion. Extracted from the seeding
// calculator so every grass surface (seeding calculator, grass comparison) reads
// one source of truth for "the seed for THIS grass".
//
// St. Augustine is intentionally unmapped — it is established from sod/plugs, not
// seed — so callers return no curated seed pick for it rather than recommend a
// seed that does not exist.

import type { CatalogPick } from './types'

// Lowercase, letters-only, collapsed-whitespace form of the grass name, so
// "Kentucky Bluegrass", "Bermudagrass", "St. Augustinegrass", and slug variants
// ("kentucky-bluegrass") all round-trip to one key.
export function normalizeGrass(grassType: string): string {
  return grassType.toLowerCase().replace(/[^a-z]+/g, ' ').replace(/\s+/g, ' ').trim()
}

// Normalized grass name -> that species' catalog seed key + why it fits.
export const SEED_BY_GRASS: Record<string, CatalogPick> = {
  'kentucky bluegrass': {
    key: 'seed-nightshift-kbg',
    reason: 'Kentucky bluegrass seed for thick, self-repairing cool-season turf with strong cold tolerance.',
  },
  'tall fescue': {
    key: 'seed-nightshift-fescue',
    reason: 'Black Beauty tall-fescue blend: deep-rooted and drought-tolerant, the cool-season workhorse.',
  },
  'fine fescue': {
    key: 'seed-fescue-jonathan-green',
    reason: 'A true fine-fescue blend (hard, chewings, creeping red) for soft, shade-tolerant, low-input turf.',
  },
  'perennial ryegrass': {
    key: 'seed-nightshift-ryegrass',
    reason: 'Perennial ryegrass with fast germination and excellent wear tolerance for cool-season lawns.',
  },
  bermudagrass: {
    key: 'seed-bermuda',
    reason: 'Heat-loving Bermuda seed for full-sun southern lawns.',
  },
  zoysiagrass: {
    key: 'seed-zoysia-zenith',
    reason: 'Seeded zoysia (Zenith) for sunny warm-season lawns. Dense, wear-tolerant turf.',
  },
  centipede: {
    key: 'seed-centipede-pennington',
    reason: 'Low-maintenance centipede seed for acidic, sunny southeastern lawns.',
  },
  bahiagrass: {
    key: 'seed-bahia-pennington',
    reason: 'Pensacola bahia seed for low-input lawns with sandy soil, heat, and drought.',
  },
  buffalograss: {
    key: 'seed-buffalo-outsidepride',
    reason: 'Seeded buffalograss for low-water, full-sun lawns in prairie and western climates.',
  },
}

// Companion: every new seeding / overseed benefits from a planting-time
// phosphorus boost.
export const STARTER: CatalogPick = {
  key: 'starter-scotts',
  reason: 'Starter fertilizer with the planting-time phosphorus boost new seedlings need to root in.',
}

// ---- Shade blends ----------------------------------------------------------------
//
// A zone's sun decides the seed as much as its grass: a Kentucky bluegrass lawn
// with a shaded side yard needs a shade blend there, not more bluegrass.
// Mirrored for the app in mobile/lib/seed-for-sun.ts (parity test).

export type ShadeSeedKind = 'fine_fescue' | 'sun_shade_mix'

export const SHADE_SEED_PICKS: Record<ShadeSeedKind, CatalogPick> = {
  fine_fescue: {
    key: 'seed-fescue-jonathan-green',
    reason: 'Fine fescues (hard, chewings, creeping red) are the most shade-tolerant cool-season seed, so shaded areas fill in instead of thinning.',
  },
  sun_shade_mix: {
    key: 'seed-cool-pennington',
    reason: 'A sun and shade mix adds fine fescue to the blend, so part-shade areas stay as thick as the sunny lawn.',
  },
}

/**
 * Which shade blend a zone's seed should be, or null when the grass's own seed
 * fits the sun. Only cool-season grasses: warm-season shade areas are sodded
 * or plugged, not seeded. Full shade takes fine fescue unless the grass already
 * handles high shade; part shade takes the mix only when the grass handles
 * little shade.
 */
export function shadeSeedKind(input: {
  season: 'Cool' | 'Warm' | null | undefined
  shadeTolerance: 'Low' | 'Medium' | 'High' | null | undefined
  sunlight: 'full_sun' | 'partial_sun' | 'shade' | null | undefined
}): ShadeSeedKind | null {
  if (input.season !== 'Cool') return null
  if (input.sunlight === 'shade') return input.shadeTolerance === 'High' ? null : 'fine_fescue'
  if (input.sunlight === 'partial_sun') return input.shadeTolerance === 'Low' ? 'sun_shade_mix' : null
  return null
}
